import React, { useEffect, useMemo, useState } from "react";
import { Container, Form, Button, Table, Alert, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import axios from "../utils/api";

const API_URL = process.env.REACT_APP_API_URL_BASE || "http://localhost:6020/";

function PricebookLookup() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [query, setQuery] = useState(initialQuery);
  const [items, setItems] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const columns = useMemo(
    () => [
      { key: "itemName", label: "Name" },
      { key: "price", label: "Price" },
      { key: "crv", label: "CRV" },
      { key: "department", label: "Department" },
    ],
    []
  );

  const fetchItems = async (pageNumber = 1, searchTerm = query) => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API_URL}api/pricebook/search`, {
        params: {
          q: searchTerm,
          page: pageNumber,
          limit,
        },
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to load pricebook");
      }

    setItems(response.data.items || []);
    setEditingItemId(null);
    setEditingValues({});
      setTotalPages(response.data.totalPages || 1);
      setPage(response.data.page || pageNumber);
    } catch (err) {
      console.error("Pricebook lookup error:", err);
      setError(err.message || "Failed to load pricebook");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (event) => {
    event.preventDefault();
    fetchItems(1, query.trim());
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    fetchItems(nextPage, query.trim());
  };

  const handleEditChange = (key, value) => {
    setEditingValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditStart = (item) => {
    setEditingItemId(item._id);
    const values = columns.reduce((acc, col) => {
      const baseValue = item[col.key] ?? item.raw?.[col.key] ?? "";
      acc[col.key] = baseValue;
      return acc;
    }, {});
    setEditingValues(values);
  };

  const handleEditCancel = () => {
    setEditingItemId(null);
    setEditingValues({});
  };

  const handleSave = async (item) => {
    try {
      if (!editingItemId || editingItemId !== item._id) {
        return;
      }
      const response = await axios.put(`${API_URL}api/pricebook/${item._id}`, editingValues);
      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to update item");
      }

      setItems((prev) =>
        prev.map((existing) => (existing._id === item._id ? response.data.item : existing))
      );
      setEditingItemId(null);
      setEditingValues({});
    } catch (err) {
      console.error("Pricebook update error:", err);
      setError(err.message || "Failed to update item");
    }
  };

  return (
    <Container className="invoice-upload-container mt-4">
      <h2 className="mb-4">Pricebook Lookup</h2>

      <div className="upload-section card p-4 mb-4">
        <h5 className="mb-3">Search Pricebook</h5>
        <Form onSubmit={handleSearch}>
          <Form.Group className="mb-3">
            <Form.Label>Search by item, UPC, SKU, vendor, or description</Form.Label>
            <Form.Control
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Enter item name, UPC, SKU..."
              disabled={loading}
            />
          </Form.Group>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Searching...
              </>
            ) : (
              "Search"
            )}
          </Button>
        </Form>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="table-section card p-4">
        <h5 className="mb-3">Results</h5>
        {loading && items.length === 0 ? (
          <div className="d-flex align-items-center">
            <Spinner animation="border" className="me-2" />
            <span>Loading pricebook...</span>
          </div>
        ) : (
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  {columns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length + 1} className="text-center">
                      No items found.
                    </td>
                  </tr>
                ) : (
                  items.map((item, index) => (
                    <tr key={item._id || index}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      {columns.map((col) => {
                        const baseValue = item[col.key] ?? item.raw?.[col.key] ?? "";
                        const displayValue = col.key === "price" && baseValue !== "" ? Number(baseValue).toFixed(2) : baseValue;
                        const isEditing = editingItemId === item._id;
                        const inputValue = isEditing ? editingValues[col.key] ?? "" : "";

                        return (
                          <td key={`${item._id || index}-${col.key}`}>
                            {isEditing ? (
                              <Form.Control
                                type="text"
                                value={inputValue}
                                onChange={(e) => handleEditChange(col.key, e.target.value)}
                              />
                            ) : (
                              displayValue
                            )}
                          </td>
                        );
                      })}
                      <td>
                        {editingItemId === item._id ? (
                          <div className="d-flex gap-2">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleSave(item)}
                            >
                              Save
                            </Button>
                            <Button variant="outline-secondary" size="sm" onClick={handleEditCancel}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button variant="outline-primary" size="sm" onClick={() => handleEditStart(item)}>
                            Edit
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <Button variant="outline-secondary" disabled={page <= 1 || loading} onClick={() => handlePageChange(page - 1)}>
            Prev
          </Button>
          <span>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline-secondary"
            disabled={page >= totalPages || loading}
            onClick={() => handlePageChange(page + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </Container>
  );
}

export default PricebookLookup;
